import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

export default function AdminReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("दोनों passwords match नहीं कर रहे");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password }, { withCredentials: true });
      setDone(true);
      toast.success("Password बदल गया — अब नए password से login करें");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-28" data-testid="admin-reset-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="admin-reset-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <ShieldCheck size={20} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold uppercase text-white">Set new password</h1>

        {!token ? (
          <p className="mt-4 rounded-xl border border-white/10 bg-[#0a0a0e] p-4 text-sm text-neutral-400" data-testid="reset-missing-token">
            Reset link अधूरा है — कृपया email में दिया पूरा link खोलें या फिर से{" "}
            <Link to="/admin/forgot" className="text-[#e6b800] underline-offset-2 hover:underline">
              reset link
            </Link>{" "}
            मँगवाएँ।
          </p>
        ) : done ? (
          <div className="mt-7 rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/5 p-5" data-testid="reset-success">
            <p className="flex items-start gap-3 text-sm leading-relaxed text-[#e6b800]">
              <Lock size={16} className="mt-0.5 shrink-0" />
              Password update हो गया। अब नए password से login करें।
            </p>
            <Link
              to="/admin"
              data-testid="reset-login-link"
              className="mt-4 inline-block rounded-full bg-[#e6b800] px-6 py-2.5 text-sm font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700]"
            >
              Login करें
            </Link>
          </div>
        ) : (
          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="reset-pass" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                नया password (min 8 characters)
              </label>
              <input
                id="reset-pass"
                data-testid="reset-password-input"
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <div>
              <label htmlFor="reset-confirm" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                Confirm password
              </label>
              <input
                id="reset-confirm"
                data-testid="reset-confirm-input"
                type="password"
                className={inputCls}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-button"
              className="w-full rounded-full bg-[#e6b800] py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update password"}
            </button>
          </div>
        )}
      </motion.form>
    </div>
  );
}
